import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { concatMap, filter, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { Store } from '@ngrx/store';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { IProject } from '@app/api/interfaces/project.interface';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectProjectEntities } from '@app/main/main-screen/store/projects/projects.selectors';
import {
  IFpLoggerNoiseSummaryState,
  initialFpLoggerNoiseSummaryState,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-summary/services/fp-logger-noise-summary/fp-logger-noise-summary.state';
import {
  FpLoggerNoiseSummaryActionGetData,
  FpLoggerNoiseSummaryActionGetDataSuccesses,
  FpLoggerNoiseSummaryActions,
  FpLoggerNoiseSummaryActionType,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-summary/services/fp-logger-noise-summary/fp-logger-noise-summary.actions';

@Injectable()
export class FpLoggerNoiseSummaryStore
  extends ComponentStore<IFpLoggerNoiseSummaryState>
  implements IGcComponentStore<FpLoggerNoiseSummaryActions>
{
  protected readonly store = inject(Store);

  constructor() {
    super(initialFpLoggerNoiseSummaryState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly loading$ = this.select(s => s.loading, { equal });

  public readonly leakDetectionMode$ = this.store.select(selectMainProjectId).pipe(
    filter((projectId): projectId is string => !!projectId),
    withLatestFrom(this.store.select(selectProjectEntities)),
    map(([projectId, projects]) => projects[projectId]),
    filter((project): project is IProject => !!project),
    map(project => project.leakDetectionMode),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public dispatch = this.effect((action$: Observable<FpLoggerNoiseSummaryActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpLoggerNoiseSummaryActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpLoggerNoiseSummaryActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({
      loading: true,
    });
  }

  private reducerGetDataSuccesses(action: FpLoggerNoiseSummaryActionGetDataSuccesses) {
    this.patchState({
      date: action.date,
      data: action.data,
      loading: false,
    });
  }

  private effectGetData(action: FpLoggerNoiseSummaryActionGetData) {
    this.dispatch({
      type: FpLoggerNoiseSummaryActionType.GetDataSuccesses,
      date: action.date,
      data: action.popupMainData,
    });
  }
}
